import { Prenotazione, ListaAttesa, BookingFormData, GiftCard, GiftCardFormData } from "../types/Booking";

const API_BASE_URL = "/api";

export interface EventoPrenotazioni {
  prenotazioni: Prenotazione[];
  listaAttesa: ListaAttesa[];
}

export interface InviteResponse {
  success: boolean;
  message: string;
  insufficientSeats?: boolean;
  postiDisponibili?: number;
  postiRichiesti?: number;
}

export const bookingService = {
  // Create a reservation
  async createPrenotazione(eventoId: number, data: BookingFormData): Promise<Prenotazione> {
    const response = await fetch(`${API_BASE_URL}/eventi/${eventoId}/prenotazioni`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella prenotazione");
    }
    return response.json();
  },

  // Add to waiting list
  async addToWaitingList(eventoId: number, data: BookingFormData): Promise<ListaAttesa> {
    const response = await fetch(`${API_BASE_URL}/eventi/${eventoId}/lista-attesa`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'iscrizione alla lista d'attesa");
    }
    return response.json();
  },

  // Get reservations and waiting list for an event (admin)
  async getEventoPrenotazioni(eventoId: number): Promise<EventoPrenotazioni> {
    const response = await fetch(`${API_BASE_URL}/eventi/${eventoId}/prenotazioni`);
    if (!response.ok) {
      throw new Error("Errore nel recupero delle prenotazioni");
    }
    return response.json();
  },

  // Delete a reservation (admin) - this cancels it (sets Annullata = 1)
  async deletePrenotazione(prenotazioneId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'eliminazione della prenotazione");
    }
  },

  // Permanently delete a cancelled reservation (admin)
  async permanentDeletePrenotazione(prenotazioneId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}/delete`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'eliminazione definitiva della prenotazione");
    }
  },

  // Delete a waiting list entry (admin)
  async deleteListaAttesa(listaAttesaId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'eliminazione dalla lista d'attesa");
    }
  },

  // Send invitation to waiting list user (admin)
  async inviteListaAttesa(listaAttesaId: number, forceInvite: boolean = false): Promise<InviteResponse> {
    const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ forceInvite }),
    });
    
    const data = await response.json();
    
    if (response.status === 409 && data.insufficientSeats) {
      return {
        success: false,
        insufficientSeats: true,
        postiDisponibili: data.postiDisponibili,
        postiRichiesti: data.postiRichiesti,
        message: data.message,
      };
    }
    
    if (!response.ok) {
      throw new Error(data.message || data || "Errore nell'invio dell'invito");
    }
    
    return { success: true, message: data.message };
  },

  // Send a reminder email to every active participant of an event
  async sendEventReminders(
    eventoId: number
  ): Promise<{ success: boolean; sent: number; failed: { email: string; error: string }[] }> {
    const response = await fetch(`${API_BASE_URL}/eventi/${eventoId}/reminder`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'invio dei promemoria");
    }
    return response.json();
  },

  // Convert a waiting list entry into a real reservation
  async convertListaAttesa(
    listaAttesaId: number,
    sendEmail: boolean
  ): Promise<{ success: boolean; emailRequested: boolean; emailSent: boolean }> {
    const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}/convert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sendEmail }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella conversione in prenotazione");
    }
    return response.json();
  },

  // Update a reservation
  async updatePrenotazione(prenotazioneId: number, data: BookingFormData): Promise<Prenotazione> {
    const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella modifica della prenotazione");
    }
    return response.json();
  },

  // Update a reservation as admin (no restrictions, optional notification email)
  async adminUpdatePrenotazione(prenotazioneId: number, data: BookingFormData, inviaEmail: boolean): Promise<Prenotazione> {
    const response = await fetch(`${API_BASE_URL}/gestione/prenotazioni/${prenotazioneId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...data, InviaEmail: inviaEmail }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella modifica della prenotazione");
    }
    return response.json();
  },

  // Update a waiting list entry
  async updateListaAttesa(listaAttesaId: number, data: BookingFormData): Promise<ListaAttesa> {
    const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella modifica dell'iscrizione alla lista d'attesa");
    }
    return response.json();
  },

  // Update a waiting list entry as admin (no restrictions)
  async adminUpdateListaAttesa(listaAttesaId: number, data: BookingFormData): Promise<ListaAttesa> {
    const response = await fetch(`${API_BASE_URL}/gestione/lista-attesa/${listaAttesaId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella modifica dell'iscrizione alla lista d'attesa");
    }
    return response.json();
  },

  // Create a gift card
  async createGiftCard(data: GiftCardFormData): Promise<GiftCard> {
    const response = await fetch(`${API_BASE_URL}/gift-cards`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nella creazione della gift card");
    }
    return response.json();
  },

  // Get all gift cards (admin)
  async getGiftCards(): Promise<GiftCard[]> {
    const response = await fetch(`${API_BASE_URL}/gift-cards`);
    if (!response.ok) {
      throw new Error("Errore nel recupero delle gift card");
    }
    return response.json();
  },

  // Verify gift card code
  async verifyGiftCard(codice: string): Promise<GiftCard | null> {
    const response = await fetch(`${API_BASE_URL}/gift-cards/verify/${codice}`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error("Errore nella verifica della gift card");
    }
    return response.json();
  },

  // Delete a gift card (admin)
  async deleteGiftCard(giftCardId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/gift-cards/${giftCardId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'eliminazione della gift card");
    }
  },

  // Approve a gift card (admin)
  async approveGiftCard(giftCardId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/gift-cards/${giftCardId}/approve`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'approvazione della gift card");
    }
  },

  // Resend confirmation email (admin)
  async resendConfirmation(prenotazioneId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}/resend-confirmation`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Errore nell'invio dell'email di conferma");
    }
    return response.json();
  },

  // Get user's own bookings and waiting list entries
  async getMyBookings(email: string): Promise<{ prenotazioni: Prenotazione[]; listaAttesa: ListaAttesa[] }> {
    const response = await fetch(`${API_BASE_URL}/my-bookings?email=${encodeURIComponent(email)}`);
    if (!response.ok) {
      throw new Error("Errore nel recupero delle prenotazioni");
    }
    return response.json();
  },
};
